/** Absolute minimum hourly rate any shift may be posted at, in USD. */
export const ABSOLUTE_RATE_FLOOR = 18;

/** Default share of the recommended (average) rate used as the floor, by profession code. */
export const DEFAULT_RATE_FLOOR_PERCENTAGES: Record<string, number> = {
  RDH: 0.8,
  DA: 0.8,
  EFDA: 0.8,
  FD: 0.8,
  DN: 0.8,
};

/** Never remove or rename top-level keys: released mobile builds read `config[professionCode]`. */
export type TRateFloorConfig = {
  [professionCode: string]: unknown;
  territories?: Record<string, Partial<Record<string, number>>>;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toPercentage = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;

/** `undefined` leaves `calculateRateFloor` on the absolute floor. */
export const resolveRateFloorPercentage = (
  config: TRateFloorConfig | undefined,
  professionCode: string,
  state?: string,
): number | undefined => {
  const territories = isPlainObject(config?.territories) ? config.territories : undefined;
  const territory = territories && state ? territories[state.trim().toUpperCase()] : undefined;
  const territoryPercentage = isPlainObject(territory)
    ? toPercentage(territory[professionCode])
    : undefined;

  return (
    territoryPercentage ??
    toPercentage(config?.[professionCode]) ??
    toPercentage(DEFAULT_RATE_FLOOR_PERCENTAGES[professionCode])
  );
};

/**
 * Lowest hourly rate selectable for a profession: the larger of the absolute
 * floor and a share of the recommended rate, rounded down to the dollar.
 * Falls back to the absolute floor when the share or average rate is unusable.
 */
export const calculateRateFloor = (averageRate: number, percentage?: number): number => {
  if (!percentage || averageRate <= 0) {
    return ABSOLUTE_RATE_FLOOR;
  }

  return Math.max(ABSOLUTE_RATE_FLOOR, Math.floor(averageRate * percentage));
};
